/**
 * All UI-chrome translations (Header, Footer, Home page).
 * Legal document bodies are co-located in each page component.
 */
const translations = {
  en: {
    /* ── Header nav ─────────────────────────────── */
    nav: {
      home: 'Home',
      terms: 'Terms',
      privacy: 'Privacy Policy',
      lending: 'Lending Terms',
      responsible: 'Responsible Use',
    },

    /* ── Home page ──────────────────────────────── */
    home: {
      heroBadge: 'Legal Documents',
      heroTitle: 'MegaTransact Legal Centre',
      heroBody:
        'Transparent terms, privacy protections, and lending conditions for MegaTransact users. These documents govern your use of the MegaTransact platform and all related services.',
      heroMeta: 'Effective Date: March 27, 2026',
      sectionHeading: 'Our Legal Documents',
      sectionSub:
        'Select a document below to read the full terms applicable to your use of MegaTransact.',
      readDoc: 'Read document',
      contactHeading: 'Have a question?',
      contactBody:
        'For any questions about these documents, please contact MegaTransact at',
      contactCompany:
        'MegaBliss LTD · Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45',
    },
    documents: [
      {
        to: '/terms-and-conditions',
        icon: '📄',
        badge: 'General',
        title: 'Terms and Conditions',
        desc: 'The rules, responsibilities, and conditions that apply when you use MegaTransact.',
      },
      {
        to: '/privacy-policy',
        icon: '🔒',
        badge: 'Privacy',
        title: 'Privacy Policy',
        desc: 'How MegaTransact collects, uses, protects, and manages your personal information.',
      },
      {
        to: '/lending-terms',
        icon: '💼',
        badge: 'Lending',
        title: 'Salary-Based Lending Terms',
        desc: "Terms governing MegaTransact's salary-based lending service — eligibility, costs, and repayment.",
      },
      {
        to: '/responsible-use',
        icon: '📋',
        badge: 'Collections',
        title: 'Responsible Use & Collections Notice',
        desc: 'How MegaTransact approaches responsible use, repayment follow-up, and collections activity.',
      },
      {
        to: '/delete-account',
        icon: '🗑️',
        badge: 'Account',
        title: 'Request Account Deletion',
        desc: 'Submit a request to permanently delete your MegaTransact account and all associated personal data.',
      },
    ],

    /* ── Footer ─────────────────────────────────── */
    footer: {
      desc: 'A digital platform providing structured salary-linked financial support and related digital services.',
      company: 'MegaBliss LTD',
      legalDocs: 'Legal Documents',
      termsLink: 'Terms and Conditions',
      privacyLink: 'Privacy Policy',
      lendingLink: 'Salary-Based Lending Terms',
      responsibleLink: 'Responsible Use & Collections',
      deleteLink: 'Request Account Deletion',
      contact: 'Contact',
      address:
        'Bâtiment EIFUN\nAvenue Kabalo n° 171, croisement Huilerie-Kabalo\nKinshasa, 4ᵉ niveau, bureau n° 45',
      copyright: '© {year} MegaTransact / MegaBliss LTD. All rights reserved.',
      lastUpdated: 'Last Updated: March 27, 2026',
    },

    /* ── Account Deletion page ──────────────────── */
    deletion: {
      label: 'Account Management',
      title: 'Request Account Deletion',
      intro:
        'You have the right to request the deletion of your MegaTransact account and all associated personal data. Please complete the form below and our team will process your request within 30 days.',
      warnTitle: 'What will be deleted:',
      warnItems: [
        'Your profile and account credentials',
        'Loans and repayment records',
        'Employee and employer linkage data',
        'All personal information held in our systems',
      ],
      warnNote:
        'Please note: regulatory and legal obligations may require us to retain certain financial records for a defined period after deletion. These retained records are securely isolated and not used for any active service.',
      emailLabel: 'Registered email address',
      emailPlaceholder: 'you@example.com',
      reasonLabel: 'Reason for deletion',
      optional: '(optional)',
      reasonPlaceholder:
        'e.g. No longer using the service, privacy concerns…',
      confirmLabel:
        'I understand that account deletion is <strong>irreversible</strong> and that all my data will be permanently removed.',
      submitBtn: 'Submit Deletion Request',
      submitting: 'Submitting…',
      altText: 'Prefer to request via email?',
      altLink: 'Send us an email',
      altSuffix: 'and include your registered email address.',
      errInvalidEmail: 'Please enter a valid email address.',
      errNoConfirm:
        'You must confirm that you understand this action is irreversible.',
      errUnavailable:
        'Our online form is temporarily unavailable. Please email your deletion request to admin@megablissworldwide.com with your registered email address.',
      errGeneric:
        'Something went wrong. Please try again or email us at admin@megablissworldwide.com.',
      successTitle: 'Request Received',
      successBody:
        'We have received your account deletion request for <strong>{email}</strong>. Our team will process your request within <strong>30 days</strong> and delete your account and all associated data from our systems. You will receive a confirmation email once the deletion is complete.',
      successNote: 'If you have any questions, contact us at',
    },

    /* ── Language toggle ────────────────────────── */
    langToggle: 'Français',
    langLabel: 'Passer en français',
    langCode: 'fr',
  },

  /* ════════════════════════════════════════════════
   *  FRENCH
   * ════════════════════════════════════════════════ */
  fr: {
    nav: {
      home: 'Accueil',
      terms: 'Conditions',
      privacy: 'Confidentialité',
      lending: 'Conditions de prêt',
      responsible: 'Utilisation responsable',
    },

    home: {
      heroBadge: 'Documents juridiques',
      heroTitle: 'Centre juridique MegaTransact',
      heroBody:
        "Conditions transparentes, protections de la vie privée et conditions de prêt pour les utilisateurs de MegaTransact. Ces documents régissent votre utilisation de la plateforme MegaTransact et de tous les services associés.",
      heroMeta: 'Date d\'entrée en vigueur : 27 mars 2026',
      sectionHeading: 'Nos documents juridiques',
      sectionSub:
        'Sélectionnez un document ci-dessous pour lire les conditions complètes applicables à votre utilisation de MegaTransact.',
      readDoc: 'Lire le document',
      contactHeading: 'Une question ?',
      contactBody:
        'Pour toute question concernant ces documents, veuillez contacter MegaTransact à',
      contactCompany:
        'MegaBliss LTD · Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45',
    },
    documents: [
      {
        to: '/terms-and-conditions',
        icon: '📄',
        badge: 'Général',
        title: "Conditions générales d'utilisation",
        desc: "Les règles, responsabilités et conditions qui s'appliquent lorsque vous utilisez MegaTransact.",
      },
      {
        to: '/privacy-policy',
        icon: '🔒',
        badge: 'Confidentialité',
        title: 'Politique de confidentialité',
        desc: 'Comment MegaTransact collecte, utilise, protège et gère vos informations personnelles.',
      },
      {
        to: '/lending-terms',
        icon: '💼',
        badge: 'Prêt',
        title: 'Conditions de prêt salarial',
        desc: "Conditions régissant le service de prêt salarial de MegaTransact — éligibilité, coûts et remboursement.",
      },
      {
        to: '/responsible-use',
        icon: '📋',
        badge: 'Recouvrements',
        title: 'Utilisation responsable et avis de recouvrement',
        desc: "Comment MegaTransact aborde l'utilisation responsable, le suivi des remboursements et les activités de recouvrement.",
      },
      {
        to: '/delete-account',
        icon: '🗑️',
        badge: 'Compte',
        title: 'Demander la suppression du compte',
        desc: 'Soumettez une demande pour supprimer définitivement votre compte MegaTransact et toutes les données personnelles associées.',
      },
    ],

    footer: {
      desc: "Une plateforme numérique fournissant un soutien financier structuré lié au salaire et des services numériques connexes.",
      company: 'MegaBliss LTD',
      legalDocs: 'Documents juridiques',
      termsLink: "Conditions générales d'utilisation",
      privacyLink: 'Politique de confidentialité',
      lendingLink: 'Conditions de prêt salarial',
      responsibleLink: 'Utilisation responsable et recouvrements',
      deleteLink: 'Demander la suppression du compte',
      contact: 'Contact',
      address:
        'Bâtiment EIFUN\nAvenue Kabalo n° 171, croisement Huilerie-Kabalo\nKinshasa, 4ᵉ niveau, bureau n° 45',
      copyright: '© {year} MegaTransact / MegaBliss LTD. Tous droits réservés.',
      lastUpdated: 'Dernière mise à jour : 27 mars 2026',
    },

    deletion: {
      label: 'Gestion du compte',
      title: 'Demander la suppression du compte',
      intro:
        'Vous avez le droit de demander la suppression de votre compte MegaTransact et de toutes les données personnelles associées. Veuillez remplir le formulaire ci-dessous et notre équipe traitera votre demande dans un délai de 30 jours.',
      warnTitle: 'Ce qui sera supprimé :',
      warnItems: [
        'Votre profil et vos identifiants de compte',
        'Les dossiers de prêts et de remboursements',
        'Les données de liaison employé et employeur',
        'Toutes les informations personnelles détenues dans nos systèmes',
      ],
      warnNote:
        "Veuillez noter : les obligations réglementaires et légales peuvent nous obliger à conserver certains dossiers financiers pendant une période définie après la suppression. Ces dossiers conservés sont isolés de manière sécurisée et ne sont pas utilisés pour un service actif.",
      emailLabel: 'Adresse e-mail enregistrée',
      emailPlaceholder: 'vous@exemple.com',
      reasonLabel: 'Raison de la suppression',
      optional: '(facultatif)',
      reasonPlaceholder:
        "ex. Je n'utilise plus le service, préoccupations liées à la vie privée…",
      confirmLabel:
        "Je comprends que la suppression du compte est <strong>irréversible</strong> et que toutes mes données seront définitivement supprimées.",
      submitBtn: 'Soumettre la demande de suppression',
      submitting: 'Envoi en cours…',
      altText: 'Vous préférez faire la demande par e-mail ?',
      altLink: 'Envoyez-nous un e-mail',
      altSuffix: 'en indiquant votre adresse e-mail enregistrée.',
      errInvalidEmail: 'Veuillez entrer une adresse e-mail valide.',
      errNoConfirm:
        "Vous devez confirmer que vous comprenez que cette action est irréversible.",
      errUnavailable:
        'Notre formulaire en ligne est temporairement indisponible. Veuillez envoyer votre demande de suppression à admin@megablissworldwide.com avec votre adresse e-mail enregistrée.',
      errGeneric:
        "Un problème est survenu. Veuillez réessayer ou nous envoyer un e-mail à admin@megablissworldwide.com.",
      successTitle: 'Demande reçue',
      successBody:
        "Nous avons reçu votre demande de suppression de compte pour <strong>{email}</strong>. Notre équipe traitera votre demande dans un délai de <strong>30 jours</strong> et supprimera votre compte et toutes les données associées de nos systèmes. Vous recevrez un e-mail de confirmation une fois la suppression terminée.",
      successNote: 'Si vous avez des questions, contactez-nous à',
    },

    langToggle: 'English',
    langLabel: 'Switch to English',
    langCode: 'en',
  },
};

export default translations;
